import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import { connect } from 'react-redux';

import { openModalAction } from '../../../actions/modal';
import { actionsAdvancedFilters } from '../../../components/AdvancedFilters/actions';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { Input } from '../../../containers/Form';
import Strings from '../../../Strings';
import { HEALTH_EVENT_VALUE_MODAL_NAME } from '../../../modals/HealthEventValueModal';
import Select from '../../../components/Select';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import actions from './redux/actions';
import {
  buildCheckboxFilterDefinition,
  getAdvancedFilterValues,
  HEALTH_DATE_FILTER_OPTIONS,
  isCustomDateRangeOption,
  resolveDateRange,
} from './shared';

import './MessagesOverview.scss';

const DEFAULT_LIMIT = 200;
const DEFAULT_DATE_FILTER = HEALTH_DATE_FILTER_OPTIONS[1];
const EVENT_DETAIL_FILTER_GROUPS = {
  org: 'healthEventDetailOrg',
  hwCode: 'healthEventDetailHwCode',
};

function TruncatedCell({ value, label, onOpen }) {
  const text = value === undefined || value === null || value === '' ? '-' : String(value);
  const tooltipId = React.useId();
  const [isViewing, setIsViewing] = React.useState(false);
  if (!onOpen || text === '-') {
    return <div className="health-event-details-table__truncated" title={text}>{text}</div>;
  }
  return (
    <>
      <button
        type="button"
        className="health-event-details-table__truncated health-event-details-table__value"
        data-tooltip-id={tooltipId}
        aria-label={Strings.formatString(Strings.healthEventFullValue, label)}
        onClick={() => {
          setIsViewing(true);
          onOpen({ label, value: text, afterClose: () => setIsViewing(false) });
        }}
      >
        {text}
      </button>
      <Tooltip id={tooltipId} hidden={isViewing} clickable className="health-event-details-tooltip" place="bottom" positionStrategy="fixed">
        {text}
      </Tooltip>
    </>
  );
}

TruncatedCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  onOpen: PropTypes.func,
};

function renderDateTimeCell(info) {
  const value = info.getValue();
  return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

function renderValueOrDashCell(info) {
  return info.getValue() || '-';
}

function renderTruncatedCell(info) {
  return <TruncatedCell value={info.getValue()} />;
}

function toCsv(values) {
  return values?.length ? values.join(',') : undefined;
}

function splitCommaSeparatedFilter(value) {
  return value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
}

function buildFilterDefinitions(bundle) {
  return {
    ...buildCheckboxFilterDefinition(
      EVENT_DETAIL_FILTER_GROUPS.org,
      'Organization',
      (bundle.organizations || []).map(org => ({ value: String(org.org_id), label: org.org_name })),
    ),
    ...buildCheckboxFilterDefinition(
      EVENT_DETAIL_FILTER_GROUPS.hwCode,
      'HW code',
      (bundle.hw_codes || []).map(hwCode => ({ value: hwCode, label: hwCode })),
    ),
  };
}

function EventDetailsTab({
  clearAllAdvancedFilters,
  drilldownRequest,
  filters,
  getEventDetails,
  getEventDetailsFormBundle,
  onBack,
  openDateRangeSelectionModal,
  openEventValueModal,
  updateAdvancedFilterState,
}) {
  const [bundle, setBundle] = React.useState({ hw_codes: [], organizations: [] });
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [pagination, setPagination] = React.useState({ offset: 0, limit: DEFAULT_LIMIT, totalRecords: 0 });
  const [sorting, setSorting] = React.useState([{ id: 'timestamp', desc: true }]);
  const [dateFilterValue, setDateFilterValue] = React.useState(DEFAULT_DATE_FILTER.value);
  const [dateRange, setDateRange] = React.useState(resolveDateRange(DEFAULT_DATE_FILTER));
  const [str1Query, setStr1Query] = React.useState('');
  const [sourceQuery, setSourceQuery] = React.useState('');
  const [peerQuery, setPeerQuery] = React.useState('');
  const [sourceMatchMode, setSourceMatchMode] = React.useState('like');
  const [peerMatchMode, setPeerMatchMode] = React.useState('like');
  const [userQuery, setUserQuery] = React.useState('');
  const [kitQuery, setKitQuery] = React.useState('');
  const requestSequence = React.useRef(0);
  const validIDs = value => !value.trim() || value.split(',').every(item => {
    const id = item.trim().replace(/^0+/, '');
    return /^[1-9][0-9]*$/.test(id) && (id.length < 19 || (id.length === 19 && id <= '9223372036854775807'));
  });
  const invalidIDs = !validIDs(userQuery) || !validIDs(kitQuery);
  const pageRequest = React.useRef({
    offset: 0,
    limit: DEFAULT_LIMIT,
    sortColumn: 'timestamp',
    sortType: 'descending',
  });

  React.useEffect(() => {
    getEventDetailsFormBundle().then(resp => {
      setBundle(resp?.response || { hw_codes: [], organizations: [] });
    });
  }, [getEventDetailsFormBundle]);

  const selectedOrgIds = React.useMemo(
    () => getAdvancedFilterValues(filters, EVENT_DETAIL_FILTER_GROUPS.org, value => Number(value)),
    [filters],
  );
  const selectedHwCodes = React.useMemo(
    () => getAdvancedFilterValues(filters, EVENT_DETAIL_FILTER_GROUPS.hwCode),
    [filters],
  );

  const loadData = React.useCallback(() => {
    const sequence = ++requestSequence.current;
    if (invalidIDs) {
      setRows([]);
      setPagination(previous => ({ ...previous, totalRecords: 0 }));
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError('');

    const params = {
      offset: pageRequest.current.offset,
      limit: pageRequest.current.limit,
      org_ids: toCsv(selectedOrgIds),
      hw_codes: toCsv(selectedHwCodes),
      source_devices: toCsv(splitCommaSeparatedFilter(sourceQuery)),
      source_device_match_mode: sourceMatchMode,
      peer_devices: toCsv(splitCommaSeparatedFilter(peerQuery)),
      peer_device_match_mode: peerMatchMode,
      user_ids: toCsv(splitCommaSeparatedFilter(userQuery)),
      kit_ids: toCsv(splitCommaSeparatedFilter(kitQuery)),
      str1_values: toCsv(splitCommaSeparatedFilter(str1Query)),
      str1_match_mode: 'exact',
      sortColumn: pageRequest.current.sortColumn,
      sortType: pageRequest.current.sortType,
    };

    const requestRange = dateFilterValue === 'custom' ? dateRange : resolveDateRange({ value: dateFilterValue });
    if (requestRange.start) {
      params.start_at = requestRange.start.clone().utc().toISOString();
    }
    if (requestRange.end) {
      params.end_at = requestRange.end.clone().utc().toISOString();
    }

    getEventDetails(params)
      .then(resp => {
        if (sequence !== requestSequence.current || resp?.type?.endsWith('/canceled')) return;
        if (resp?.error || resp?.type?.endsWith('/error')) {
          setRows([]);
          setPagination(previous => ({ ...previous, totalRecords: 0 }));
          setError(resp?.response?.status === 403 ? Strings.healthEventPermissionError : Strings.healthEventLoadError);
          setIsLoading(false);
          return;
        }
        const response = resp?.response || {};
        setRows(response.data || []);
        setPagination({
          offset: response.offset || 0,
          limit: response.limit || pageRequest.current.limit,
          totalRecords: response.totalRecords || 0,
        });
        setIsLoading(false);
      })
      .catch(() => {
        if (sequence !== requestSequence.current) return;
        setRows([]);
        setError(Strings.healthEventLoadError);
        setIsLoading(false);
      });
  }, [dateFilterValue, dateRange.end, dateRange.start, getEventDetails, invalidIDs, kitQuery, peerMatchMode, peerQuery, selectedHwCodes, selectedOrgIds, sourceMatchMode, sourceQuery, str1Query, userQuery]);

  React.useEffect(() => {
    pageRequest.current.offset = 0;
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    if (!drilldownRequest?.key) {
      return;
    }

    clearAllAdvancedFilters();

    (drilldownRequest.org_ids || []).forEach(value => {
      updateAdvancedFilterState({ group: EVENT_DETAIL_FILTER_GROUPS.org, value: String(value), state: true });
    });
    (drilldownRequest.hw_codes || []).forEach(value => {
      updateAdvancedFilterState({ group: EVENT_DETAIL_FILTER_GROUPS.hwCode, value, state: true });
    });

    setDateFilterValue('custom');
    setDateRange({
      start: drilldownRequest.start_at ? moment(drilldownRequest.start_at) : undefined,
      end: drilldownRequest.end_at ? moment(drilldownRequest.end_at) : undefined,
    });
    setStr1Query((drilldownRequest.str1_values || []).join(', '));
    setSourceQuery((drilldownRequest.source_devices || []).join(', '));
    setPeerQuery((drilldownRequest.peer_devices || []).join(', '));
    setSourceMatchMode(drilldownRequest.source_device_match_mode || 'exact');
    setPeerMatchMode(drilldownRequest.peer_device_match_mode || 'exact');
    setUserQuery((drilldownRequest.user_ids || []).join(', '));
    setKitQuery((drilldownRequest.kit_ids || []).join(', '));
    setSorting([{ id: 'timestamp', desc: true }]);
    pageRequest.current.sortColumn = 'timestamp';
    pageRequest.current.sortType = 'descending';
  }, [clearAllAdvancedFilters, drilldownRequest, updateAdvancedFilterState]);

  const filterDefinitions = React.useMemo(() => buildFilterDefinitions(bundle), [bundle]);

  const onDateRangeChanged = React.useCallback(option => {
    if (isCustomDateRangeOption(option)) {
      const onDateSubmited = dates => {
        setDateFilterValue('custom');
        setDateRange({
          start: dates.startDate ? moment(dates.startDate * 1000) : undefined,
          end: dates.endDate ? moment(dates.endDate * 1000) : undefined,
        });
      };
      openDateRangeSelectionModal({ actionOnDone: onDateSubmited });
      return;
    }

    setDateFilterValue(option?.value || DEFAULT_DATE_FILTER.value);
    setDateRange(resolveDateRange(option || DEFAULT_DATE_FILTER));
  }, [openDateRangeSelectionModal]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('timestamp', {
        header: 'Timestamp',
        cell: renderDateTimeCell,
        size: 180,
      }),
      columnHelper.accessor('event_timestamp', {
        header: 'Event timestamp',
        cell: renderDateTimeCell,
        size: 180,
      }),
      columnHelper.accessor('source_device', {
        header: Strings.healthEventSourceDevice,
        cell: renderTruncatedCell,
        size: 220,
      }),
      columnHelper.accessor('peer_device', {
        header: Strings.healthEventPeerDevice,
        cell: renderTruncatedCell,
        size: 220,
      }),
      columnHelper.accessor('data', {
        header: Strings.healthEventData,
        cell: info => <TruncatedCell value={info.getValue()} label={Strings.healthEventData} onOpen={openEventValueModal} />,
        size: 360,
        enableSorting: false,
      }),
      columnHelper.accessor('dataj', {
        header: Strings.healthEventDataJ,
        cell: info => <TruncatedCell value={info.getValue()} label={Strings.healthEventDataJ} onOpen={openEventValueModal} />,
        size: 360,
        enableSorting: false,
      }),
      columnHelper.accessor('hw_code', {
        header: 'HW code',
        cell: renderValueOrDashCell,
        size: 120,
        enableSorting: false,
      }),
      columnHelper.accessor('org_name', {
        header: 'Organization',
        cell: renderValueOrDashCell,
        size: 180,
        enableSorting: false,
      }),
      columnHelper.accessor('user_id', {
        header: Strings.healthEventUserID,
        cell: renderValueOrDashCell,
        size: 110,
        enableSorting: false,
      }),
      columnHelper.accessor('kit_id', {
        header: Strings.kitId,
        cell: renderValueOrDashCell,
        size: 110,
        enableSorting: false,
      }),
      columnHelper.accessor('str1', {
        header: 'Str1',
        cell: renderTruncatedCell,
        size: 220,
        enableSorting: false,
      }),
    ],
    [openEventValueModal],
  );

  const onSortingChange = React.useCallback(sortingState => {
    if (!sortingState || sortingState.length === 0) {
      setSorting([{ id: 'timestamp', desc: true }]);
      pageRequest.current.sortColumn = 'timestamp';
      pageRequest.current.sortType = 'descending';
      pageRequest.current.offset = 0;
      return;
    }

    const nextSort = sortingState[0];
    setSorting([{ id: nextSort.id, desc: nextSort.desc }]);
    pageRequest.current.sortColumn = nextSort.id;
    pageRequest.current.sortType = nextSort.desc ? 'descending' : 'ascending';
    pageRequest.current.offset = 0;
  }, []);

  return (
    <>
      <PageHeader
        onBackClick={onBack}
        left="Event details"
        right={(
          <div className="health-messages__table-header-actions">
            <Select
              id="health-event-details-date-range"
              data={HEALTH_DATE_FILTER_OPTIONS}
              value={dateFilterValue}
              onChange={onDateRangeChanged}
            />
            <AdvancedFilters>
              <Filter definition={filterDefinitions[EVENT_DETAIL_FILTER_GROUPS.org]} />
              <Filter definition={filterDefinitions[EVENT_DETAIL_FILTER_GROUPS.hwCode]} />
            </AdvancedFilters>
            <Button class="health-refresh-button" onClick={loadData} title="Refresh" />
          </div>
        )}
      />
      <div className="health-event-details-filters health-event-details-search">
        <div className="health-event-details-filters__row">
          <Input id="health-event-source" name="healthEventSource" label={Strings.healthEventSourceDevice} value={sourceQuery}
            onChange={event => { setSourceQuery(event.target.value); setSourceMatchMode('like'); }} />
          <Input id="health-event-peer" name="healthEventPeer" label={Strings.healthEventPeerDevice} value={peerQuery}
            onChange={event => { setPeerQuery(event.target.value); setPeerMatchMode('like'); }} />
          <Input id="health-event-user" name="healthEventUser" label={Strings.healthEventUserID} value={userQuery}
            onChange={event => setUserQuery(event.target.value)} />
          <Input id="health-event-kit" name="healthEventKit" label={Strings.kitId} value={kitQuery}
            onChange={event => setKitQuery(event.target.value)} />
        </div>
        {invalidIDs ? <div role="alert">{Strings.healthEventInvalidIDs}</div> : null}
        {str1Query ? (
          <button type="button" className="health-event-details-device-filters__chip" onClick={() => setStr1Query('')}>
            {Strings.formatString(Strings.healthEventFailCodeFilter, str1Query)}
          </button>
        ) : null}
      </div>
      <AdvancedFiltersBar customFilters={filterDefinitions} />

      {error ? <div className="health-messages-error">{error}</div> : null}

      <Table3
        name="health-event-details"
        data={rows}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        sorting={sorting}
        onOffsetChange={offset => {
          pageRequest.current.offset = offset;
          loadData();
        }}
        onPageSizeChange={limit => {
          pageRequest.current.limit = limit;
          pageRequest.current.offset = 0;
          loadData();
        }}
        onSortingChange={sortingState => {
          onSortingChange(sortingState);
          loadData();
        }}
      />
    </>
  );
}

EventDetailsTab.propTypes = {
  clearAllAdvancedFilters: PropTypes.func.isRequired,
  drilldownRequest: PropTypes.shape({
    key: PropTypes.string,
    start_at: PropTypes.string,
    end_at: PropTypes.string,
    org_ids: PropTypes.arrayOf(PropTypes.number),
    hw_codes: PropTypes.arrayOf(PropTypes.string),
    str1_values: PropTypes.arrayOf(PropTypes.string),
    source_devices: PropTypes.arrayOf(PropTypes.string),
    peer_devices: PropTypes.arrayOf(PropTypes.string),
    source_device_match_mode: PropTypes.string,
    peer_device_match_mode: PropTypes.string,
    user_ids: PropTypes.arrayOf(PropTypes.number),
    kit_ids: PropTypes.arrayOf(PropTypes.number),
  }),
  filters: PropTypes.array.isRequired,
  getEventDetails: PropTypes.func.isRequired,
  getEventDetailsFormBundle: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  openDateRangeSelectionModal: PropTypes.func.isRequired,
  openEventValueModal: PropTypes.func.isRequired,
  updateAdvancedFilterState: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  clearAllAdvancedFilters: () => dispatch(actionsAdvancedFilters.clearAll()),
  getEventDetails: params => dispatch(actions.getEventDetails(params)),
  getEventDetailsFormBundle: () => dispatch(actions.getEventDetailsFormBundle()),
  openEventValueModal: data => dispatch(openModalAction(HEALTH_EVENT_VALUE_MODAL_NAME, data)),
  openDateRangeSelectionModal: data => dispatch(openModalAction('data-range-selection-modalion', data)),
  updateAdvancedFilterState: data => dispatch(actionsAdvancedFilters.updateFilterState(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EventDetailsTab);
